import { clsx } from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Status } from '../../../js/api';
import { Button } from '../components/button';
import { StatusCodes } from '../constants/workflows';
import { StatusMetadata, WorkflowStatus } from '../hooks/useWorkflowMetadata';
import { getStatusButtonStyle } from './statusStyles';
import { useWorkflowAccess } from './workflowAccess';

type StatusTransitionButtonsProps = {
  isLoading: boolean;
  currentStatusCode: string | undefined;
  workflowMetadata: StatusMetadata;
  onStatusUpdate?: (status: Status) => void;
  onStatusClick?: (status: Status) => void;
  isPending?: boolean;
  small?: boolean;
  /** Map of status codes to disabled state. true = disabled, false/missing = enabled. */
  disabledStatusMap?: Partial<Record<StatusCodes, boolean>>;
  /** Map of status codes to excluded state. true = excluded (hidden), false/missing = shown. */
  excludedStatusMap?: Partial<Record<StatusCodes, boolean>>;
  className?: string;
};

export const StatusTransitionButtons = ({
  isLoading,
  currentStatusCode,
  workflowMetadata,
  onStatusUpdate,
  onStatusClick,
  isPending = false,
  small = false,
  disabledStatusMap,
  excludedStatusMap,
  className = '',
}: StatusTransitionButtonsProps): React.ReactElement => {
  const { renderableStatusesFrom } = useWorkflowAccess(workflowMetadata);
  const renderableStatuses: WorkflowStatus[] = renderableStatusesFrom(currentStatusCode);

  if (isLoading) {
    return <></>;
  }
  if (!currentStatusCode || !workflowMetadata) {
    return <></>;
  }

  // Use onStatusClick if provided (opens modal pattern), otherwise fall back to onStatusUpdate
  const handleClick = onStatusClick || onStatusUpdate;
  if (!handleClick) {
    console.warn(
      'StatusTransitionButtons: Either onStatusClick or onStatusUpdate must be provided'
    );
    return <></>;
  }

  // Audience filtering handled by useWorkflowAccess

  return (
    <div className={twMerge(clsx('tw:flex tw:gap-2', className))}>
      {renderableStatuses
        .filter((status) => !excludedStatusMap?.[status.code as StatusCodes])
        .map((status) => (
          <Button
            key={status.code}
            color={getStatusButtonStyle(status)}
            onClick={() => handleClick(status)}
            disabled={isPending || !!disabledStatusMap?.[status.code as StatusCodes]}
            small={small}
          >
            {status.verb || status.title}
          </Button>
        ))}
    </div>
  );
};
